/**
 * Tons de badge, séparés du composant.
 *
 * Fichier dédié pour que `Badge.tsx` n'exporte que des composants : le Fast
 * Refresh de Vite ne conserve l'état d'un module que si celui-ci n'exporte
 * rien d'autre. Mélanger constantes et composants provoque un rechargement
 * complet de la page à chaque modification.
 */
export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'muted';

export const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'border-navy-200/70 bg-navy-50 text-navy-800',
  accent: 'border-accent-200/70 bg-accent-50 text-accent-800',
  success: 'border-emerald-200/70 bg-emerald-50 text-emerald-800',
  warning: 'border-amber-200/70 bg-amber-50 text-amber-800',
  /**
   * Réservé à un échec, distinct de `warning` : l'ambre signale un point
   * d'attention, le rouge une action qui n'a pas abouti. Les confondre rendrait
   * l'un des deux illisible.
   */
  danger: 'border-red-200/70 bg-red-50 text-red-800',
  muted: 'border-ink-200 bg-ink-100 text-ink-500',
};

/**
 * Correspondance statut -> ton visuel.
 *
 * Indexée sur le `code` du référentiel et non sur le libellé : renommer
 * « Prospect » en « Piste » ne doit pas faire perdre sa couleur au badge.
 * Un code inconnu retombe sur `neutral` — une valeur ajoutée par un
 * administrateur s'affiche donc correctement, simplement sans couleur dédiée.
 */
const STATUS_TONES: Record<string, BadgeTone> = {
  CLIENT: 'success',
  PROSPECT: 'accent',
  PARTENAIRE: 'neutral',
  IMPORT: 'muted',
};

export const toneForStatus = (code: string | undefined): BadgeTone =>
  (code ? STATUS_TONES[code] : undefined) ?? 'neutral';
